/** @format */
import React, { FC, useEffect, useMemo } from 'react'
import {
  Card,
  Descriptions,
  DescriptionItem,
  Icon,
  Table,
  Copy,
  TableColumn,
  Message,
  Badge
} from '@king-design/react'
import styled from 'styled-components'
import { useCopyToClipboard } from 'react-use'
import { getPrometheusInstanceToken } from 'src/api/ApiPromethuesInstance'

export interface PrometheusInstanceInfo {
  UserID: number
  InstanceName: string
  ClusterID: string
  ClusterName: string
  ClusterType: number
  ClusterState: string
  AccessType: string
  ControllerID: string
  Region: string
  RegionCnName: string
  Token: string
  StorageDuration: number
  FederateEndpoint: string
  FederateInnerEndpoint: string
  HttpApiEndpoint: string
  HttpApiInnerEndpoint: string
  CreatedAt: string
  UpdatedAt: string
}

interface PrometheusInstanceInfoProps {
  className?: string
  instanceInfo: PrometheusInstanceInfo
}

// 支持Remote Write的用户ID
const remoteWriteUserIDs: number[] = [
  73406822, 73401193, 2000159590, 2000170364, 2000179683, 2000091781, 73398680, 2000169742
]

const PrometheusInstance: FC<PrometheusInstanceInfoProps> = ({ className, instanceInfo }) => {
  const [copyState, copyToClipboard] = useCopyToClipboard()

  // 复制Token
  const onCopyToken = async () => {
    if (instanceInfo?.ControllerID) {
      const result = await getPrometheusInstanceToken(instanceInfo?.ControllerID)
      if (result?.Token) {
        copyToClipboard(result?.Token)
      } else {
        Message.error('获取Token失败')
      }
    }
  }

  // 复制成功/失败后提示
  useEffect(() => {
    const { error, value } = copyState
    if (value) {
      if (error) {
        Message.error('复制失败')
      } else {
        Message.success('复制成功')
      }
    }
  }, [copyState])

  const remoteWriteUrl = useMemo(() => {
    let region: string = instanceInfo?.Region
    if (instanceInfo?.UserID === 73398680) {
      region = 'cn-northwest-2'
    }
    return `http://${region}.internal.prometheus.ksyun.com/write/${instanceInfo?.ClusterID}/${instanceInfo?.UserID}/${instanceInfo?.ControllerID}/prometheus/api/v1/write`
  }, [instanceInfo])

  return instanceInfo ? (
    <div className={className}>
      <Card title={instanceInfo?.ClusterName} type="border">
        <Descriptions title="基础信息" className="base_info">
          <DescriptionItem label="实例名称">{instanceInfo?.ClusterName || '-'}</DescriptionItem>
          <DescriptionItem label="实例ID">{instanceInfo?.ControllerID || '-'}</DescriptionItem>
          <DescriptionItem label="Region">{instanceInfo?.RegionCnName || '-'}</DescriptionItem>
          <DescriptionItem label="创建时间">{instanceInfo?.CreatedAt || '-'}</DescriptionItem>
          <DescriptionItem label="数据存储时长">
            {instanceInfo?.StorageDuration ? instanceInfo?.StorageDuration + '天' : '-'}
          </DescriptionItem>
        </Descriptions>
        <Descriptions title="服务地址">
          <DescriptionItem label="token">
            <div style={{ paddingTop: 4 }}>
              <span>********</span>
              <Icon className="k-icon-clone" hoverable onClick={onCopyToken} />
            </div>
          </DescriptionItem>
        </Descriptions>
        <p className="sub_titme">Federate地址</p>
        <Table
          checkType="none"
          data={[
            {
              network: '公网',
              url: instanceInfo?.FederateEndpoint || '-'
            },
            {
              network: '内网',
              url: instanceInfo?.FederateInnerEndpoint || '-'
            }
          ]}
          rowKey={data => data.network}
        >
          <TableColumn key="network" width={100} title="网络" />
          <TableColumn
            key="url"
            title="url"
            slotTemplate={([data]) => {
              return (
                <div className="url_td">
                  <span>{data.url}</span>
                  <Copy text={data?.url}></Copy>
                </div>
              )
            }}
          />
        </Table>
        {remoteWriteUserIDs.includes(instanceInfo?.UserID) ? (
          <>
            <p className="sub_titme">
              Remote Write地址 <Badge text="Beta版" />
            </p>
            <Table
              checkType="none"
              data={[
                {
                  network: '内网',
                  url: remoteWriteUrl
                }
              ]}
              rowKey={data => data.network}
            >
              <TableColumn key="network" width={100} title="网络" />
              <TableColumn
                key="url"
                title="url"
                slotTemplate={([data]) => {
                  return (
                    <div className="url_td">
                      <span>{data.url}</span>
                      <Copy text={data?.url}></Copy>
                    </div>
                  )
                }}
              />
            </Table>
          </>
        ) : null}

        <>
          <p className="sub_titme">HTTP API地址</p>
          <Table
            checkType="none"
            data={[
              {
                network: '公网',
                url: instanceInfo?.HttpApiEndpoint || '-'
              },
              {
                network: '内网',
                url: instanceInfo?.HttpApiInnerEndpoint || '-'
              }
            ]}
            rowKey={data => data.network}
          >
            <TableColumn key="network" width={100} title="网络" />
            <TableColumn
              key="url"
              title="url"
              slotTemplate={([data]) => {
                return (
                  <div className="url_td">
                    <span>{data.url}</span>
                    <Copy text={data?.url}></Copy>
                  </div>
                )
              }}
            />
          </Table>
        </>
      </Card>
    </div>
  ) : null
}

export default styled(PrometheusInstance)`
  margin-top: 16px;
  .base_info {
    border-bottom: 1px solid rgb(229, 229, 229);
    margin-bottom: 16px;
  }
  .k-icon-clone {
    margin-left: 8px;
  }
  .sub_titme {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    .k-badge {
      margin-top: -2px;
    }
  }
  .k-table {
    margin-bottom: 12px;
  }
  .url_td {
    display: flex;
    align-items: center;
    .k-icon {
      margin-left: 8px;
      cursor: pointer;
    }
  }
`
